import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../../store/store';
import { issueAPI } from '../API/issueAPI';
import {
    entityCluster,
    entityClusterBundle,
    entityRegistration,
} from '../../../interfaces/generic-entity.interface';
import {
    governmentAnnouncement,
    IssueGazette,
    issueInfo,
    issueSummary,
    keywordMentions,
    Tweet,
} from '../interface/issue-entity.interface';
import axios from 'axios';
import saveAs from 'file-saver';
import { recentUpdates } from '../../shared/interfaces/whats-new.interface';
import { rangedSummary } from 'features/shared/interfaces/text-summary.interface';
import {
    CorrelatedEntityCommunication,
    EntityCommunicationActivity,
    EntityCommunicationActivityBreakdown,
    EntityCommunicationMonthlyAggregate,
} from 'interfaces/entity-communication.interface';

interface IssueState {
    announcements: governmentAnnouncement[] | null;
    announcements_loading: boolean;

    updates: recentUpdates | null;
    updates_loading: boolean;
    updates_fail: boolean;

    mentions: keywordMentions[] | null;
    mentions_loading: boolean;
    mentions_fail: boolean;

    issue_info: issueInfo | null;
    issue_info_loading: boolean;
    issue_info_fail: boolean;

    subject_matter_details_summary: rangedSummary | null;
    subject_matter_details_summary_loading: boolean;

    mp_social_overview: any[] | null;
    mp_social_overview_loading: boolean;

    senator_social_overview: any[] | null;
    senator_social_overview_loading: boolean;

    sentences: any[] | null;
    sentences_loading: boolean;
    sentences_fail: boolean;

    recent_tweets: Tweet[] | null;
    recent_tweets_loading: boolean;

    lobbying_activity: CorrelatedEntityCommunication[] | null;
    lobbying_activity_loading: boolean;
    lobbying_activity_fail: boolean;

    recent_reg: entityRegistration[] | null;
    recent_reg_loading: boolean;
    recent_reg_fail: boolean;

    recent_dereg: entityRegistration[] | null;
    recent_dereg_loading: boolean;
    recent_dereg_fail: boolean;

    mentions_cluster: entityCluster | null;
    mentions_cluster_loading: boolean;
    mentions_cluster_fail: boolean;

    top_entities: {
        topSectors: EntityCommunicationActivity[] | null;
        topInstitutions: EntityCommunicationActivity[] | null;
        topFirms: EntityCommunicationActivity[] | null;
    };
    top_entities_loading: boolean;

    top_sector_activity: EntityCommunicationActivityBreakdown[] | null;
    top_sector_activity_loading: boolean;

    all_sector_activity: EntityCommunicationMonthlyAggregate[] | null;
    all_sector_activity_loading: boolean;

    org_activity: EntityCommunicationActivity[] | null;
    org_activity_loading: boolean;

    dpoh_activity: EntityCommunicationActivity[] | null;
    dpoh_activity_loading: boolean;

    mp_activity: EntityCommunicationActivity[] | null;
    mp_activity_loading: boolean;

    org_cluster: entityCluster | null;
    org_cluster_loading: boolean;
    org_cluster_fail: boolean;

    sector_cluster: entityCluster | null;
    sector_cluster_loading: boolean;
    sector_cluster_fail: boolean;

    inst_cluster: entityCluster | null;
    inst_cluster_loading: boolean;
    inst_cluster_fail: boolean;

    firm_cluster: entityCluster | null;
    firm_cluster_loading: boolean;
    firm_cluster_fail: boolean;

    dpoh_cluster: entityCluster | null;
    dpoh_cluster_loading: boolean;
    dpoh_cluster_fail: boolean;

    subject_cluster: entityCluster | null;
    subject_cluster_loading: boolean;
    subject_cluster_fail: boolean;

    gazette: IssueGazette | null;
    gazette_loading: boolean;

    issue_summary: issueSummary | null;
    issue_summary_loading: boolean;
    issue_summary_fail: boolean;

    export_fail: boolean;
    export_loading: boolean;
    create_loading: boolean;
}

const initialState: IssueState = {
    announcements: null,
    announcements_loading: false,

    updates: null,
    updates_loading: false,
    updates_fail: false,

    mentions: null,
    mentions_loading: false,
    mentions_fail: false,

    issue_info: null,
    issue_info_loading: false,
    issue_info_fail: false,

    subject_matter_details_summary: null,
    subject_matter_details_summary_loading: false,

    mp_social_overview: null,
    mp_social_overview_loading: false,

    senator_social_overview: null,
    senator_social_overview_loading: false,

    sentences: null,
    sentences_loading: false,
    sentences_fail: false,

    recent_tweets: null,
    recent_tweets_loading: false,

    lobbying_activity: null,
    lobbying_activity_loading: false,
    lobbying_activity_fail: false,

    recent_reg: null,
    recent_reg_loading: false,
    recent_reg_fail: false,

    recent_dereg: null,
    recent_dereg_loading: false,
    recent_dereg_fail: false,

    top_entities: {
        topSectors: null,
        topInstitutions: null,
        topFirms: null,
    },
    top_entities_loading: false,

    all_sector_activity: null,
    all_sector_activity_loading: false,

    top_sector_activity: null,
    top_sector_activity_loading: false,

    org_activity: null,
    org_activity_loading: false,

    dpoh_activity: null,
    dpoh_activity_loading: false,

    mp_activity: null,
    mp_activity_loading: false,

    mentions_cluster: null,
    mentions_cluster_loading: false,
    mentions_cluster_fail: false,

    org_cluster: null,
    org_cluster_loading: false,
    org_cluster_fail: false,

    sector_cluster: null,
    sector_cluster_loading: false,
    sector_cluster_fail: false,

    inst_cluster: null,
    inst_cluster_loading: false,
    inst_cluster_fail: false,

    firm_cluster: null,
    firm_cluster_loading: false,
    firm_cluster_fail: false,

    dpoh_cluster: null,
    dpoh_cluster_loading: false,
    dpoh_cluster_fail: false,

    subject_cluster: null,
    subject_cluster_loading: false,
    subject_cluster_fail: false,

    gazette: null,
    gazette_loading: false,

    issue_summary: null,
    issue_summary_loading: false,
    issue_summary_fail: false,

    export_fail: false,
    export_loading: false,
    create_loading: false,
};

// Fetch relevant announcements for the current issue
export const fetchIssueGovernmentAnnouncements = createAsyncThunk(
    'issue/fetchIssueGovernmentAnnouncements',
    async ({ code, date }: { code: string; date: string }): Promise<governmentAnnouncement[]> =>
        issueAPI.fetchGovernmentAnnouncements(code, date)
);

// Fetch the recent updates for the current issue
export const fetchIssueUpdates = createAsyncThunk(
    'issue/fetchIssueUpdates',
    async ({ code, date }: { code: string; date: string }): Promise<recentUpdates> =>
        issueAPI.fetchUpdates(code, date)
);

// Fetch the summary for the current issue
export const fetchIssueSummary = createAsyncThunk(
    'issue/fetchIssueSummary',
    async ({ code, date }: { code: string; date: string }): Promise<any> =>
        issueAPI.fetchIssueSummary(code, date)
);

// Fetch the recent tweets for the current issue
export const fetchIssueTweets = createAsyncThunk(
    'issue/fetchIssueTweets',
    async ({ code, date }: { code: string; date: string }): Promise<Tweet[]> =>
        issueAPI.fetchTweets(code, date)
);

// Fetch the yearly overview data for the current issue
export const fetchKeywordMentions = createAsyncThunk(
    'issue/fetchKeywordMentions',
    async ({ code, date }: { code: string; date: string }): Promise<any[]> =>
        issueAPI.fetchKeywordMentions(code, date)
);

// Fetch the issue info
export const fetchIssueInfo = createAsyncThunk(
    'issue/fetchIssueInfo',
    async ({ code }: { code: string }): Promise<issueInfo> => issueAPI.fetchInfo(code)
);

// Fetch the subject matter details summary for the current issue
export const fetchSubjectMatterDetailsSummary = createAsyncThunk(
    'issue/fetchSubjectMatterDetailsSummary',
    async ({ code, date }: { code: string; date: string }): Promise<rangedSummary> =>
        issueAPI.fetchSubjectMatterDetails(code, date)
);

// Fetch the mp tweet overview for the current issue
export const fetchIssueMpSocialOverview = createAsyncThunk(
    'issue/fetchIssueMpSocialOverview',
    async ({ code, date }: { code: string; date: string }): Promise<any[]> =>
        issueAPI.fetchSocialOverview(code, date, false)
);

// Fetch the senator tweet overview for the current issue
export const fetchIssueSenatorSocialOverview = createAsyncThunk(
    'issue/fetchIssueSenatorSocialOverview',
    async ({ code, date }: { code: string; date: string }): Promise<any[]> =>
        issueAPI.fetchSocialOverview(code, date, true)
);

// Fetch the issue sentences
export const fetchSentences = createAsyncThunk(
    'issue/fetchSentences',
    async ({ code, date }: { code: string; date: string }): Promise<any[]> =>
        issueAPI.fetchSentences(code, date)
);

// Fetch the recent lobbying activity data for the current issue
export const fetchIssueRecentLobbying = createAsyncThunk(
    'issue/fetchIssueRecentLobbying',
    async ({
        code,
        date,
    }: {
        code: string;
        date: string;
    }): Promise<{
        lobbyingActivity: CorrelatedEntityCommunication[];
        allSectorActivity: EntityCommunicationMonthlyAggregate[];
        topThreeSectorActivity: EntityCommunicationActivityBreakdown[];
        orgActivity: EntityCommunicationActivity[];
        dpohActivity: EntityCommunicationActivity[];
        mpActivity: EntityCommunicationActivity[];
    }> => issueAPI.fetchRecentLobbyingActivity(code, date)
);

export const fetchIssueProcessedCommunications = createAsyncThunk(
    'issue/fetchIssueProcessedCommunications',
    async ({
        code,
        date,
    }: {
        code: string;
        date: string;
    }): Promise<{
        topSectors: EntityCommunicationActivity[];
        topInstitutions: EntityCommunicationActivity[];
        topFirms: EntityCommunicationActivity[];
    }> => issueAPI.fetchProcessedCommunications(code, date)
);

// Fetch the recent lobbying registrations in the current issue
export const fetchIssueRegistrations = createAsyncThunk(
    'issue/fetchIssueRegistrations',
    async ({ code, date }: { code: string; date: string }): Promise<entityRegistration[]> =>
        issueAPI.fetchRecentRegistrations(code, date)
);

// Fetch the recent lobbying registrations in the current issue
export const fetchIssueDeregistrations = createAsyncThunk(
    'issue/fetchIssueDeregistrations',
    async ({ code, date }: { code: string; date: string }): Promise<entityRegistration[]> =>
        issueAPI.fetchRecentDeregistrations(code, date)
);

// Fetch the mentions cluster data
export const fetchMentionCluster = createAsyncThunk(
    'issue/fetchMentionCluster',
    async ({ code, date }: { code: string; date: string }): Promise<entityCluster> =>
        issueAPI.fetchCluster(code, 'mentions', date)
);

export const fetchClusters = createAsyncThunk(
    'issue/fetchClusters',
    async ({ code, date }: { code: string; date: string }): Promise<entityClusterBundle> =>
        issueAPI.fetchClusterBundle(+code, date)
);

// Fetch the gazette data
export const fetchGazette = createAsyncThunk(
    'issue/fetchGazette',
    async ({ code, date }: { code: number; date: string }): Promise<IssueGazette> =>
        issueAPI.fetchGazette(+code, date)
);

// Create new or update existing custom issue
export const createOrUpdateIssue = createAsyncThunk(
    'issue/createOrUpdateIssue',
    async ({
        name,
        keywords,
        id,
    }: {
        name: string;
        keywords: string[];
        id: number | null;
    }): Promise<any> => {
        const response = await issueAPI.createOrUpdateIssue(name, keywords, id);
        return response.data;
    }
);

// Create new or update existing custom issue
export const deleteIssue = createAsyncThunk(
    'issue/deleteIssue',
    async (id: number): Promise<void> => {
        await issueAPI.deleteIssue(id);
    }
);

export const exportIssueDashboard = createAsyncThunk(
    'issue/exportDashboard',
    async (data: any): Promise<void> => {
        const response = await axios.post(
            `${process.env.REACT_APP_PDF_SERVICE_URL}/issue/report`,
            data,
            {
                responseType: 'blob',
            }
        );

        const pdfEncoding = response.data;

        const blob = new Blob([pdfEncoding], { type: 'application/pdf' });

        saveAs(blob, 'LobbyIQ-Issue');
    }
);

const issueSlice = createSlice({
    name: 'issueReducer',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchIssueGovernmentAnnouncements.pending, (state) => {
                state.announcements_loading = true;
                state.announcements = null;
            })
            .addCase(fetchIssueGovernmentAnnouncements.fulfilled, (state, action) => {
                state.announcements_loading = false;
                state.announcements = action.payload;
            })
            .addCase(fetchIssueGovernmentAnnouncements.rejected, (state) => {
                state.announcements_loading = false;
                state.announcements = null;
            })
            .addCase(fetchIssueUpdates.pending, (state) => {
                state.updates_loading = true;
                state.updates = null;
            })
            .addCase(fetchIssueUpdates.fulfilled, (state, action) => {
                state.updates_loading = false;
                state.updates_fail = false;
                state.updates = action.payload;
            })
            .addCase(fetchIssueUpdates.rejected, (state) => {
                state.updates_loading = false;
                state.updates_fail = true;
                state.updates = null;
            })
            .addCase(fetchIssueSummary.pending, (state) => {
                state.issue_summary_loading = true;
                state.issue_summary = null;
            })
            .addCase(fetchIssueSummary.fulfilled, (state, action) => {
                state.issue_summary_loading = false;

                state.issue_summary = action.payload;
            })
            .addCase(fetchIssueSummary.rejected, (state) => {
                state.issue_summary_loading = false;
                state.issue_summary = null;
            })
            .addCase(fetchIssueTweets.pending, (state) => {
                state.recent_tweets_loading = true;
                state.recent_tweets = null;
            })
            .addCase(fetchIssueTweets.fulfilled, (state, action) => {
                state.recent_tweets_loading = false;
                state.recent_tweets = action.payload;
            })
            .addCase(fetchIssueTweets.rejected, (state) => {
                state.recent_tweets_loading = false;
                state.recent_tweets = null;
            })
            .addCase(fetchKeywordMentions.pending, (state) => {
                state.mentions_loading = true;
            })
            .addCase(fetchKeywordMentions.fulfilled, (state, action) => {
                state.mentions_loading = false;
                state.mentions_fail = false;
                state.mentions = action.payload;
            })
            .addCase(fetchKeywordMentions.rejected, (state) => {
                state.mentions_loading = false;
                state.mentions_fail = true;
                state.mentions = null;
            })
            .addCase(fetchIssueInfo.pending, (state) => {
                state.issue_info_loading = true;
            })
            .addCase(fetchIssueInfo.fulfilled, (state, action) => {
                state.issue_info_loading = false;
                state.issue_info_fail = false;
                state.issue_info = action.payload;
            })
            .addCase(fetchIssueInfo.rejected, (state) => {
                state.issue_info_loading = false;
                state.issue_info_fail = true;
                state.issue_info = null;
            })
            .addCase(fetchSubjectMatterDetailsSummary.pending, (state) => {
                state.subject_matter_details_summary_loading = true;
                state.subject_matter_details_summary = null;
            })
            .addCase(fetchSubjectMatterDetailsSummary.fulfilled, (state, action) => {
                state.subject_matter_details_summary_loading = false;
                state.subject_matter_details_summary = action.payload;
            })
            .addCase(fetchSubjectMatterDetailsSummary.rejected, (state) => {
                state.subject_matter_details_summary_loading = false;
                state.subject_matter_details_summary = null;
            })
            .addCase(fetchIssueMpSocialOverview.pending, (state) => {
                state.mp_social_overview_loading = true;
            })
            .addCase(fetchIssueMpSocialOverview.fulfilled, (state, action) => {
                state.mp_social_overview_loading = false;
                state.mp_social_overview = action.payload;
            })
            .addCase(fetchIssueMpSocialOverview.rejected, (state) => {
                state.mp_social_overview_loading = false;
                state.mp_social_overview = null;
            })
            .addCase(fetchIssueSenatorSocialOverview.pending, (state) => {
                state.senator_social_overview_loading = true;
            })
            .addCase(fetchIssueSenatorSocialOverview.fulfilled, (state, action) => {
                state.senator_social_overview_loading = false;
                state.senator_social_overview = action.payload;
            })
            .addCase(fetchIssueSenatorSocialOverview.rejected, (state) => {
                state.senator_social_overview_loading = false;
                state.senator_social_overview = null;
            })
            .addCase(fetchSentences.pending, (state) => {
                state.sentences_loading = true;
            })
            .addCase(fetchSentences.fulfilled, (state, action) => {
                state.sentences_loading = false;
                state.sentences_fail = false;
                state.sentences = action.payload;
            })
            .addCase(fetchSentences.rejected, (state) => {
                state.sentences_loading = false;
                state.sentences_fail = true;
                state.sentences = null;
            })
            .addCase(fetchIssueRecentLobbying.pending, (state) => {
                state.lobbying_activity_loading = true;
                state.lobbying_activity = null;
                state.top_sector_activity_loading = true;
                state.top_sector_activity = null;
                state.all_sector_activity_loading = true;
                state.all_sector_activity = null;
                state.org_activity_loading = true;
                state.org_activity = null;
                state.dpoh_activity_loading = true;
                state.dpoh_activity = null;
                state.mp_activity_loading = true;
                state.mp_activity = null;
            })
            .addCase(fetchIssueRecentLobbying.fulfilled, (state, action) => {
                state.lobbying_activity_loading = false;
                state.lobbying_activity_fail = false;
                state.lobbying_activity = action.payload.lobbyingActivity;
                state.top_sector_activity_loading = false;
                state.top_sector_activity = action.payload.topThreeSectorActivity;
                state.all_sector_activity_loading = false;
                state.all_sector_activity = action.payload.allSectorActivity;
                state.org_activity_loading = false;
                state.org_activity = action.payload.orgActivity;
                state.dpoh_activity_loading = false;
                state.dpoh_activity = action.payload.dpohActivity;
                state.mp_activity_loading = false;
                state.mp_activity = action.payload.mpActivity;
            })
            .addCase(fetchIssueRecentLobbying.rejected, (state) => {
                state.lobbying_activity_loading = false;
                state.lobbying_activity_fail = true;
                state.lobbying_activity = null;
                state.top_sector_activity_loading = false;
                state.top_sector_activity = null;
                state.all_sector_activity_loading = false;
                state.all_sector_activity = null;
                state.org_activity_loading = false;
                state.org_activity = null;
                state.dpoh_activity_loading = false;
                state.dpoh_activity = null;
                state.mp_activity_loading = false;
                state.mp_activity = null;
            })
            .addCase(fetchIssueProcessedCommunications.pending, (state) => {
                state.top_entities_loading = true;
                state.top_entities.topSectors = null;
                state.top_entities.topInstitutions = null;
                state.top_entities.topFirms = null;
            })
            .addCase(fetchIssueProcessedCommunications.fulfilled, (state, action) => {
                state.top_entities_loading = false;
                state.top_entities.topSectors = action.payload.topSectors;
                state.top_entities.topInstitutions = action.payload.topInstitutions;
                state.top_entities.topFirms = action.payload.topFirms;
            })
            .addCase(fetchIssueProcessedCommunications.rejected, (state) => {
                state.top_entities_loading = false;
                state.top_entities.topSectors = null;
                state.top_entities.topInstitutions = null;
                state.top_entities.topFirms = null;
            })
            .addCase(fetchIssueRegistrations.pending, (state) => {
                state.recent_reg_loading = true;
                state.recent_reg = null;
            })
            .addCase(fetchIssueRegistrations.fulfilled, (state, action) => {
                state.recent_reg_loading = false;
                state.recent_reg_fail = false;
                state.recent_reg = action.payload;
            })
            .addCase(fetchIssueRegistrations.rejected, (state) => {
                state.recent_reg_loading = false;
                state.recent_reg_fail = true;
                state.recent_reg = null;
            })
            .addCase(fetchIssueDeregistrations.pending, (state) => {
                state.recent_dereg_loading = true;
                state.recent_dereg = null;
            })
            .addCase(fetchIssueDeregistrations.fulfilled, (state, action) => {
                state.recent_dereg_loading = false;
                state.recent_dereg_fail = false;
                state.recent_dereg = action.payload;
            })
            .addCase(fetchIssueDeregistrations.rejected, (state) => {
                state.recent_dereg_loading = false;
                state.recent_dereg_fail = true;
                state.recent_dereg = null;
            })
            .addCase(fetchMentionCluster.pending, (state) => {
                state.mentions_cluster_loading = true;
                state.mentions_cluster = null;
            })
            .addCase(fetchMentionCluster.fulfilled, (state, action) => {
                state.mentions_cluster_loading = false;
                state.mentions_cluster_fail = false;
                state.mentions_cluster = action.payload;
            })
            .addCase(fetchMentionCluster.rejected, (state) => {
                state.org_cluster_loading = false;
                state.org_cluster_fail = true;
                state.org_cluster = null;
            })

            .addCase(fetchClusters.pending, (state) => {
                state.subject_cluster_loading = true;
                state.sector_cluster_loading = true;
                state.org_cluster_loading = true;
                state.inst_cluster_loading = true;
                state.firm_cluster_loading = true;
                state.dpoh_cluster_loading = true;
            })
            .addCase(fetchClusters.fulfilled, (state, action) => {
                state.subject_cluster_loading = false;
                state.sector_cluster_loading = false;
                state.org_cluster_loading = false;
                state.inst_cluster_loading = false;
                state.firm_cluster_loading = false;
                state.dpoh_cluster_loading = false;

                const clusterBundle = action.payload;

                if (clusterBundle.subject) {
                    state.subject_cluster = clusterBundle.subject;
                }
                if (clusterBundle.firm) {
                    state.firm_cluster = clusterBundle.firm;
                }
                if (clusterBundle.sector) {
                    state.sector_cluster = clusterBundle.sector;
                }
                if (clusterBundle.organization) {
                    state.org_cluster = clusterBundle.organization;
                }
                if (clusterBundle.institution) {
                    state.inst_cluster = clusterBundle.institution;
                }
                if (clusterBundle.dpoh) {
                    state.dpoh_cluster = clusterBundle.dpoh;
                }
            })
            .addCase(fetchClusters.rejected, (state) => {
                state.subject_cluster_loading = false;
                state.subject_cluster_fail = true;
                state.subject_cluster = null;
                state.firm_cluster = null;
                state.sector_cluster = null;
                state.org_cluster = null;
                state.inst_cluster = null;
                state.dpoh_cluster = null;
            })

            .addCase(fetchGazette.pending, (state) => {
                state.gazette_loading = true;
                state.gazette = null;
            })
            .addCase(fetchGazette.fulfilled, (state, action) => {
                state.gazette_loading = false;
                state.gazette = action.payload;
            })
            .addCase(fetchGazette.rejected, (state) => {
                state.gazette_loading = false;
                state.gazette = null;
            })
            .addCase(exportIssueDashboard.pending, (state) => {
                state.export_loading = true;
            })
            .addCase(exportIssueDashboard.fulfilled, (state, action) => {
                state.export_loading = false;
                state.export_fail = false;
            })
            .addCase(exportIssueDashboard.rejected, (state) => {
                state.export_loading = false;
                state.export_fail = true;
            })
            .addCase(createOrUpdateIssue.pending, (state) => {
                state.create_loading = true;
            })
            .addCase(createOrUpdateIssue.fulfilled, (state, action) => {
                state.create_loading = false;
            })
            .addCase(createOrUpdateIssue.rejected, (state) => {
                state.create_loading = false;
            });
    },
});

export const selectIssueUpdates = (state: RootState) => state.issueData.updates;
export const selectIssueUpdatesLoading = (state: RootState) => state.issueData.updates_loading;

export const selectIssueGovernmentAnnouncements = (state: RootState) =>
    state.issueData.announcements;
export const selectIssueGovernmentAnnouncementsLoading = (state: RootState) =>
    state.issueData.announcements_loading;

export const selectIssueTweets = (state: RootState) => state.issueData.recent_tweets;
export const selectIssueTweetsLoading = (state: RootState) => state.issueData.recent_tweets_loading;

export const selectIssueKeywordMentions = (state: RootState) => state.issueData.mentions;
export const selectIssueKeywordMentionsLoading = (state: RootState) =>
    state.issueData.mentions_loading;

export const selectIssueInfo = (state: RootState) => state.issueData.issue_info;
export const selectIssueInfoLoading = (state: RootState) => state.issueData.issue_info_loading;

export const selectIssueSubjectMatterDetailsSummary = (state: RootState) =>
    state.issueData.subject_matter_details_summary;
export const selectIssueSubjectMatterDetailsSummaryLoading = (state: RootState) =>
    state.issueData.subject_matter_details_summary_loading;

export const selectIssueMpSocialOverview = (state: RootState) => state.issueData.mp_social_overview;
export const selectIssueMpSocialOverviewLoading = (state: RootState) =>
    state.issueData.mp_social_overview_loading;

export const selectIssueSenatorSocialOverview = (state: RootState) =>
    state.issueData.senator_social_overview;
export const selectIssueSenatorSocialOverviewLoading = (state: RootState) =>
    state.issueData.senator_social_overview_loading;

export const selectIssueSentences = (state: RootState) => state.issueData.sentences;
export const selectIssueSentencesLoading = (state: RootState) => state.issueData.sentences_loading;

export const selectIssueRecentLobbying = (state: RootState) => state.issueData.lobbying_activity;
export const selectIssueRecentLobbyingLoading = (state: RootState) =>
    state.issueData.lobbying_activity_loading;

export const selectIssueRegistrations = (state: RootState) => state.issueData.recent_reg;
export const selectIssueRegistrationsLoading = (state: RootState) =>
    state.issueData.recent_reg_loading;

export const selectIssueDeregistrations = (state: RootState) => state.issueData.recent_dereg;
export const selectIssueDeregistrationsLoading = (state: RootState) =>
    state.issueData.recent_dereg_loading;

export const selectIssueTopEntities = (state: RootState) => state.issueData.top_entities;
export const selectIssueTopEntitiesLoading = (state: RootState) =>
    state.issueData.top_entities_loading;

export const selectIssueAllSectorActivity = (state: RootState) =>
    state.issueData.all_sector_activity;
export const selectIssueAllSectorActivityLoading = (state: RootState) =>
    state.issueData.all_sector_activity_loading;

export const selectIssueTopSectorActivity = (state: RootState) =>
    state.issueData.top_sector_activity;
export const selectIssueTopSectorActivityLoading = (state: RootState) =>
    state.issueData.top_sector_activity_loading;

export const selectIssueOrgActivity = (state: RootState) => state.issueData.org_activity;
export const selectIssueOrgActivityLoading = (state: RootState) =>
    state.issueData.org_activity_loading;

export const selectIssueDpohActivity = (state: RootState) => state.issueData.dpoh_activity;
export const selectIssueDpohActivityLoading = (state: RootState) =>
    state.issueData.dpoh_activity_loading;

export const selectIssueMpActivity = (state: RootState) => state.issueData.mp_activity;
export const selectIssueMpActivityLoading = (state: RootState) =>
    state.issueData.mp_activity_loading;

export const selectMentionCluster = (state: RootState) => state.issueData.mentions_cluster;
export const selectMentionClusterLoading = (state: RootState) =>
    state.issueData.mentions_cluster_loading;

export const selectOrganizationCluster = (state: RootState) => state.issueData.org_cluster;
export const selectOrganizationClusterLoading = (state: RootState) =>
    state.issueData.org_cluster_loading;

export const selectSectorCluster = (state: RootState) => state.issueData.sector_cluster;
export const selectSectorClusterLoading = (state: RootState) =>
    state.issueData.sector_cluster_loading;

export const selectInstitutionCluster = (state: RootState) => state.issueData.inst_cluster;
export const selectInstitutionClusterLoading = (state: RootState) =>
    state.issueData.inst_cluster_loading;

export const selectFirmCluster = (state: RootState) => state.issueData.firm_cluster;
export const selectFirmClusterLoading = (state: RootState) => state.issueData.firm_cluster_loading;

export const selectDPOHCluster = (state: RootState) => state.issueData.dpoh_cluster;
export const selectDPOHClusterLoading = (state: RootState) => state.issueData.dpoh_cluster_loading;

export const selectSubjectCluster = (state: RootState) => state.issueData.subject_cluster;
export const selectSubjectClusterLoading = (state: RootState) =>
    state.issueData.subject_cluster_loading;

export const selectGazette = (state: RootState) => state.issueData.gazette;
export const selectGazetteLoading = (state: RootState) => state.issueData.gazette_loading;

export const selectIssueSummary = (state: RootState) => state.issueData.issue_summary;
export const selectIssueSummaryLoading = (state: RootState) =>
    state.issueData.issue_summary_loading;

export const selectIssueExportLoading = (state: RootState) => state.issueData.export_loading;
export const selectIssueExportFail = (state: RootState) => state.issueData.export_fail;

export const selectCreateIssueLoading = (state: RootState) => state.issueData.create_loading;

export const issueData = (state: RootState) => state.issueData;

export default issueSlice.reducer;
